/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FilterService } from 'primeng/api';
import { Observable } from 'rxjs';

export type Unboxed<T> = T extends (infer U)[] ? U : T;

export type TableActiveSelectionMode = 'single' | 'multiple';
export type TableSelectionMode = 'none' | TableActiveSelectionMode;

export type SelectType<M, T> = M extends 'single' ? T : M extends 'multiple' ? T[] : undefined;

export type FilterType =
  | 'EMPTY'
  | 'NOT_EMPTY'
  | 'CONTAIN' // property array contain compared value
  | 'IN' // compared array contains property value
  | 'IN_ARRAY' // compared array contains every value of property array
  | 'ARRAY_EMPTY'
  | 'ARRAY_NOT_EMPTY'
  | 'CONTAIN_STR' // property value (string) contains compared value
  | 'EQUAL'
  | 'LOWER'
  | 'STRICT_LOWER'
  | 'GREATER'
  | 'STRICT_GREATER';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterFunction = (val: any, compareValue?: any) => boolean;

const filterService = new FilterService();
export const filterFonctionMap = new Map<FilterType, FilterFunction>()
  .set('EMPTY', (val: string) => val === '' || val === undefined || val === null)
  .set('NOT_EMPTY', (val: string) => val !== '' && val !== undefined && val !== null)
  .set('EQUAL', <T extends string | number, F extends T>(val: T, compareValue: F) => val === compareValue)
  .set(
    'CONTAIN',
    <T extends string | number, F extends T>(valArray: T[], compareValue: F) =>
      valArray != null && valArray.includes(compareValue)
  )
  .set(
    'IN',
    <T extends string | number>(val: T, compareValueArray: T[]) => val != null && compareValueArray.includes(val)
  )
  .set('IN_ARRAY', <T extends string | number, F extends T>(val: T[], compareValue: F[]) =>
    compareValue.every((e) => val.includes(e))
  )
  .set('ARRAY_EMPTY', <T extends string | number>(val: T[]) => val.length === 0)
  .set('ARRAY_NOT_EMPTY', <T extends string | number>(val: T[]) => val.length > 0)
  .set('CONTAIN_STR', (val: string, compareValue: string) => filterService.filters['contains'](val, compareValue))
  .set('LOWER', (val: number, compareValue: number) => filterService.filters['lte'](val, compareValue))
  .set('STRICT_LOWER', (val: number, compareValue: number) => filterService.filters['lt'](val, compareValue))
  .set('GREATER', (val: number, compareValue: number) => filterService.filters['gte'](val, compareValue))
  .set('STRICT_GREATER', (val: number, compareValue: number) => filterService.filters['gt'](val, compareValue));

/** return true if reference value contains searched value ignoring case */
export const containsIgnoreCase = (refValue: unknown, searchedValue: string) =>
  filterService.filters['contains'](refValue, searchedValue);

export interface FilterValue<T, S extends keyof T = keyof T> {
  id: string;
  property?: S;
  value?: Unboxed<T[S]> | T[S] | T[S][];
  filterFunction: FilterFunction;
}

export type FilterOptionType = 'single' | 'and' | 'or';

export interface AbstractFilterOption<T> {
  type: FilterOptionType;
  translationKey: string;
  id: string;
  filterValue?: FilterValue<T>;
  subFilters?: AbstractFilterOption<T>[];
}

export interface SingleFilterOption<T> extends AbstractFilterOption<T> {
  type: 'single';
  count?: number;
  filterValue: FilterValue<T>;
  isDefault: boolean;
}

export interface OrFilterOption<T> extends AbstractFilterOption<T> {
  type: 'or';
  subFilters: AbstractFilterOption<T>[];
  hasTranslation: boolean;
  superAdminOnly: boolean;
  isExtendedByDefault: boolean;
}

export interface AndFilterOption<T> extends AbstractFilterOption<T> {
  type: 'and';
  subFilters: AbstractFilterOption<T>[];
  hasTranslation: boolean;
  superAdminOnly: boolean;
  isExtendedByDefault: boolean;
}

export const isSingleFilterOption = <T>(filter: AbstractFilterOption<T>): filter is SingleFilterOption<T> => {
  return filter.type === 'single';
};

export const isOrFilterOption = <T>(filter: AbstractFilterOption<T>): filter is OrFilterOption<T> => {
  return filter.type === 'or';
};

export const isAndFilterOption = <T>(filter: AbstractFilterOption<T>): filter is AndFilterOption<T> => {
  return filter.type === 'and';
};

export const isMultipleFilterOption = <T>(
  filter: AbstractFilterOption<T>
): filter is AndFilterOption<T> | OrFilterOption<T> => isAndFilterOption(filter) || isOrFilterOption(filter);

export interface MultipleFilterCreation<T> {
  translationKey: string;
  filters: AbstractFilterOption<T>[];
  filterId?: string;
  hasTranslation?: boolean;
  superAdminOnly?: boolean;
  isExtendedByDefault?: boolean;
}

export interface SingleFilterCreation<T, S extends keyof T = keyof T> {
  translationKey: string;
  property: S;
  filterType: FilterType;
  value?: Unboxed<T[S]> | T[S] | T[S][];
  isDefault?: boolean;
  filterId?: string;
}

export interface SingleFilterCustomCreation {
  translationKey: string;
  filterFunction: FilterFunction;
  filterId?: string;
  isDefault: boolean;
}

export interface OptionFiltersAction {
  key: string;
  isSuperAdmin?: boolean;
  label$: Observable<string>;
  dataQA: string;
  selected$: Observable<boolean>;
  onClick: () => void;
  count$: Observable<number>;
}
